import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ControlledTextField } from 'src/lib/itaptec/components_forms/src/components/forms/text-field/text-field';
import CardActions from 'src/pages/sistema/components/CardActions';
import { useSubmitOperacion } from 'src/pages/sistema/hooks/useSubmitOperacion';
import { ContextOperacion, ContextOperacionType } from 'src/pages/sistema/RefactorLayout';
import { EntityWithIdFields } from 'src/types';
import { PermissionType } from 'src/types/security';
import { HttpAFOCL } from '../api/httoAFOCL';
import { AFOCLHeader, AFOCLRow, useAFOCLTablaValor } from '../hooks/useTablaValorAFOCL';
import { FormUpdateTablaValores, OperacionAFOCLTablaValorVista, schemaFormUpdateTablaValores } from '../types/dto';
import { OperacionAFOCLFields, OperacionAFOCLTablaValorFields } from '../types/fields';
import { OperacionAFOCL } from '../types/types';
import AFOCLTablaValoresEditorGrid from './AFOCLTablaValoresEditorGrid ';
import { AFOCLTablaValoresHistorialDialog } from './AFOCLTablaValoresHistorialDialog';
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba';

interface SeccionAFOCLTablaValoresProps {
  operacion: OperacionAFOCL;
  codSistemaPrincipal: number;
}

export const SeccionAFOCLTablaValores = ({ operacion, codSistemaPrincipal }: SeccionAFOCLTablaValoresProps) => {
  const { permisoTipo } = useContext<ContextOperacionType>(ContextOperacion)
  const { submitOperacion } = useSubmitOperacion();
  const [isEditing, setIsEditing] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);

  const { headers, rows, isLoading, refetch } = useAFOCLTablaValor(
    operacion[EntityWithIdFields.Id]
  );

  const methods = useForm<FormUpdateTablaValores>({
    resolver: zodResolver(schemaFormUpdateTablaValores),
    defaultValues: {
      [OperacionAFOCLFields.Observaciones]: '',
      [OperacionAFOCLFields.TablaValores]: []
    }
  });

  const handleEdit = () => {
    const listaPlana: OperacionAFOCLTablaValorVista[] = [];
    // "unpivot" de los datos que pivotea el hook
    rows.forEach(row => {
      Object.values(row.celdas).forEach((celda) => {
        listaPlana.push(celda as OperacionAFOCLTablaValorVista);
      });
    });

    methods.reset({
      [OperacionAFOCLFields.Observaciones]: '',
      [OperacionAFOCLFields.TablaValores]: listaPlana
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    methods.reset();
  };

  const onSave = async (data: FormUpdateTablaValores) => {
    await submitOperacion(data, {
      apiCall: (payload) => HttpAFOCL.updateTablaValores(operacion[EntityWithIdFields.Id], payload),
      codSistemaPrincipal: codSistemaPrincipal,
      successMessage: 'Tabla de valores actualizada correctamente',
      navigateTo: null,
      onSuccess: async () => {
        await refetch();
        setIsEditing(false);
      }
    });
  };



  return (
    <CardActions
      title='Tabla de Valores'
      icon={isEditing ? 'mdi:table-edit' : 'mdi:table-check'}
      colapsable
      tableContent
      actions={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistorialDialogOpen(true)}
            sx={{ textTransform: 'none' }}
            variant={"contained"}
          // disabled={statusHistoria === QueryStatus.Loading}
          >
            Historial
          </Button>

          {permisoTipo >= PermissionType.Write && !isEditing ? (
            <Button variant="outlined" size="small" onClick={handleEdit} startIcon={<Icon icon="mdi:pencil" />}>
              Editar
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" color="secondary" size="small" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" onClick={methods.handleSubmit(onSave)} startIcon={<SaveIcon />}>
                Guardar Cambios
              </Button>
            </Box>
          )}
        </Box>
      }
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : isEditing ?
        // edicion
        (
          <FormProvider {...methods}>
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <ControlledTextField
                fullWidth
                label="Observaciones"
                control={methods.control}
                name={OperacionAFOCLFields.Observaciones}
                multiline
                rows={2}
              />
              <AFOCLTablaValoresEditorGrid
                formMethods={methods}
                headers={headers}
                rows={rows}
                aforos={methods.watch(OperacionAFOCLFields.TablaValores as any)}
                codPais={codSistemaPrincipal}
                isLoading={isLoading}
              />
            </Box>
          </FormProvider>
        ) :
        // vista
        (
          <AFOCLTablaValores
            headers={headers}
            rows={rows}
          />
        )}

      {
        historialDialogOpen &&
        <AFOCLTablaValoresHistorialDialog idOperacion={operacion[EntityWithIdFields.Id]} open={historialDialogOpen} onClose={() => setHistorialDialogOpen(false)} />
      }
    </CardActions>
  );
};

interface AFOCLTablaValoresProps {
  headers: AFOCLHeader[];
  rows: AFOCLRow[];

}

export const AFOCLTablaValores = ({ headers, rows }: AFOCLTablaValoresProps) => {
  const theme = useTheme();
  
  const getStyleForValue = (valor: string | undefined) => {
    if (valor === 'No Aplica') {
      return { color: '#999', fontStyle: 'italic' as const, textAlign: 'center' as const };
    }
    return { textAlign: 'center' as const };
  };

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto', boxShadow: 'none' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>Instrumento</TableCell>
            {headers.map((header) => (
              <TableCell key={`h-${header.codOperacionTipo}`} colSpan={2} align="center" sx={{ fontWeight: 'bold' }}>
                {header.descOperacionTipo}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {headers.map((header) => (
              <React.Fragment key={`s-${header.codOperacionTipo}`}>
                <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: '#f5f5f5' }}>Puente</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: '#f5f5f5' }}>Cliente</TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={`row-${rowIndex}`} hover>
              <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>{row.descInstrumento}</TableCell>
              {headers.map((header) => {
                const celda = row.celdas[header.codOperacionTipo];
                const vP = celda ? celda[OperacionAFOCLTablaValorFields.ValorPuente] : '-';
                const vC = celda ? celda[OperacionAFOCLTablaValorFields.ValorCliente] : '-';

                const isDifferent = vP !== vC;

                return (
                  <React.Fragment key={`c-${rowIndex}-${header.codOperacionTipo}`}>
                    <TableCell sx={getStyleForValue(vP)}>{vP}</TableCell>
                    {/* <TableCell sx={getStyleForValue(vC || '')}>{vC}</TableCell> */}

                    <TableCell sx={{
                      ...getStyleForValue(vC || ''),
                      ...(isDifferent ? {
                        border: '1px solid',
                        borderColor: 'warning.main',
                        backgroundColor: hexToRGBA(theme.palette.warning.light, 0.1),
                      } : {})
                    }}>
                      {vC}
                    </TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}